import { zodResolver } from "@hookform/resolvers/zod";
import { FilterOutline24Icon } from "@vygruppen/spor-icon-react";
import { getLocalStorageTodoFilterSettings } from "api/dropsLocalStorage";
import { withErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import TodoComponent from "features/TodoOploggSidebar/Todo/TodoComponent";
import { RoleOptions } from "features/TodoOploggSidebar/Todo/TodoInput/RoleOptions";
import { VehicleSetOptions } from "features/TodoOploggSidebar/Todo/TodoInput/VehicleSetOptions";
import {
  ContainerFlex,
  TopFlex,
} from "features/TodoOploggSidebar/Todo/TodoStyles";
import TodoFilter from "features/TodoOploggSidebar/Todo/todoFilter/TodoFilter";
import useTodoFilterState, {
  defaultTodoFilterState,
} from "features/TodoOploggSidebar/Todo/todoFilter/useTodoFilterState";
import useTodo from "features/TodoOploggSidebar/Todo/useTodo";
import { InputTextarea } from "features/TodoOploggSidebar/common/InputTextarea";
import { ShowIfTrue } from "features/TodoOploggSidebar/common/ShowIfTrue";
import { getDefaultReceivingRoles } from "features/TodoOploggSidebar/common/utils";
import { TodoForm, todoForm } from "features/TodoOploggSidebar/formSchema";
import { useEffect, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Button,
  Field,
  Flex,
  HStack,
  Separator,
  Text,
} from "shared/components/Spor";
import { SectionButton } from "shared/components/buttons/SectionButton";
import { Dropdown } from "shared/components/forms/Dropdown";
import { useFormVerbose } from "shared/form/useFormVerbose";
import { useUserInfo } from "shared/hooks/useUserInfo";
import { getSelectedTodoRoleCount, getTodoRole } from "shared/types/roles";
import { useDropsRole } from "stores/useDropsRole";
import { useTheme } from "styled-components";

const Todo = () => {
  const { role: currentRole } = useDropsRole();
  const { t } = useTranslation();
  const { email, displayName } = useUserInfo();
  const [messaging, setMessaging] = useState(false);

  const { post, postStatus } = useTodo();

  const theme = useTheme();

  const {
    todoFilterState,
    setTodoFilterState,
    toggleRoles,
    toggleVehicleSetTypes,
    toggleTodoStatus,
  } = useTodoFilterState(getLocalStorageTodoFilterSettings());

  const activeFilterCount =
    todoFilterState.vehicleSetTypes.length +
    todoFilterState.todoStatus.filter((s) => s !== "incomplete").length +
    getSelectedTodoRoleCount(todoFilterState.roles);

  const formMethods = useFormVerbose<TodoForm>({
    resolver: zodResolver(todoForm),
    defaultValues: {
      description: "",
      roles: getDefaultReceivingRoles(currentRole),
    },
  });

  const { reset, handleSubmit } = formMethods;

  const onSubmit = handleSubmit((data) => {
    const roles = data.roles || [];
    const vehicleSetTypes = data.vehicleSetTypes || [];

    const username = email || "";
    const todoData = {
      description: data.description,
      roles,
      vehicleSetTypes: vehicleSetTypes.join(","),
      username,
      displayName,
    };

    post(todoData);

    reset();
    setTimeout(() => {
      setMessaging(false);
    }, 500);
  });

  useEffect(() => {
    formMethods.setValue("roles", getDefaultReceivingRoles(currentRole));
  }, [currentRole]);

  return (
    <ContainerFlex flexDir="column" overflowY="auto" w="100%">
      <TopFlex p="24px 12px 18px 12px" alignItems="center">
        {/* <Text fontWeight="bold">Todo</Text> Keep until next iteration, will be used again then */}
        <Flex width="100%" justifyContent="space-between">
          <SectionButton
            checked={messaging}
            variant="primary"
            onCheckedChange={() =>
              setMessaging((currentMessaging) => !currentMessaging)
            }
            style={{ marginRight: "10px" }}
            height="42px"
            label={t("Ny to-do")}
            alerts={0}
          />
          <Dropdown
            icon={<FilterOutline24Icon />}
            selectedAmount={activeFilterCount}
            height="42px"
            data-testid="filter-button-todo"
          >
            <TodoFilter
              todoFilterState={todoFilterState}
              setTodoFilterState={setTodoFilterState}
              clearTodoFilterState={() =>
                setTodoFilterState({
                  ...defaultTodoFilterState,
                  roles: getTodoRole(currentRole),
                })
              }
              toggleRoles={toggleRoles}
              toggleVehicleSetTypes={toggleVehicleSetTypes}
              toggleTodoStatus={toggleTodoStatus}
            />
          </Dropdown>
        </Flex>
      </TopFlex>

      <Separator borderTop={`1px solid ${theme.colorOutlineSecondary}`} />

      <FormProvider {...formMethods}>
        <ShowIfTrue show={messaging}>
          <form
            onSubmit={onSubmit}
            style={{
              padding: "18px",
              position: "relative",
              backgroundColor: theme.colorBackgroundMain,
              border: `1px solid ${theme.colorOutlineSecondary}`,
              borderRadius: "6px",
              outline: `2px solid ${theme.colorBackgroundBase}`,
              zIndex: "9",
              marginTop: "6px",
            }}
          >
            <Field>
              <Text fontWeight="bold" paddingBottom="6px">
                {t("Ny to-do")}
              </Text>
              <InputTextarea
                label={t("Skriv inn tekst her...")}
                formField="description"
              />
            </Field>
            <HStack pt={2} width="100%">
              <RoleOptions />
              <VehicleSetOptions />
            </HStack>
            <Flex marginTop="20px" alignItems="center">
              <Button
                type="submit"
                size="sm"
                variant="primary"
                loading={postStatus === "pending"}
                ml="auto"
                data-testid="submit-button-todo"
              >
                {t("Opprett")}
              </Button>
            </Flex>
          </form>
        </ShowIfTrue>
      </FormProvider>

      <TodoComponent
        todoFilterState={todoFilterState}
        setTodoFilterState={setTodoFilterState}
      />
    </ContainerFlex>
  );
};

export default withErrorBoundary(Todo);
